// ═══════════════════════════════════════════════════════════════════════════
//  THE ONE LIVE REGION ON A BALLOT PAPER — AND IT CANNOT SAY WHAT YOU MARKED.
//
//  It takes ONE prop, and that prop is a closed union of ACT tokens. There is no
//  string prop, no `children`, no `ReactNode`, no message, no code and no
//  interpolation anywhere in this file. **A caller cannot pass content in, and
//  that is the point** — the sentence is chosen HERE, from a fixed table, by a
//  word that names what happened rather than what was chosen.
//
//  WHY THE SHAPE IS THE CONTROL. CAST-PATH.md:213-220 forbids two things by
//  name: a confirmation that restates the vote ("the highest shoulder-surf
//  moment in the flow — the phone held loosely while the person looks up, a
//  neighbour six inches away"), and any live region that echoes the selection
//  ("the well-intentioned implementation would speak a secret ballot aloud in a
//  room full of the people it is secret from"). The correct parity is the
//  opposite of the intuitive one: **neither user is shown their vote after
//  submit.** A `notice: string` prop obeys that rule only for as long as every
//  future caller remembers it. A union of tokens obeys it because there is no
//  channel.
//
//  ⚠ NO MACHINE GATE IN THIS REPO CAN CATCH A REGRESSION HERE. `role="status"`
//  is valid ARIA and `eslint-plugin-jsx-a11y` APPROVES it — correctly, since the
//  fault is not an accessibility fault. The axe scan will not see it either: a
//  live region echoing a true string is exactly what a live region is for. What
//  a human reviewer should look for, and it is checkable in seconds:
//
//    1. Does `CastAct` still contain only string-literal members — no arm with a
//       payload, no `string`, no `code`, no `label`?
//    2. Does `CastStatus`'s props type still consist of exactly `act: CastAct`?
//    3. Is `ACT_COPY` still a `Record<CastAct, string>` of LITERALS, with no
//       template interpolation in any value?
//    4. Does any OTHER element in this package carry `role="status"`,
//       `role="alert"` or `aria-live` on the cast path, and can the string
//       inside it be derived from a mark?
//
//  Three of those four are a grep. The fourth is the one to read carefully.
// ═══════════════════════════════════════════════════════════════════════════

import type { JSX } from 'react';

/**
 * What just happened — never what was chosen.
 *
 * Every arm is an ACT or a state of the request. Note in particular what is NOT
 * here: no `marked`, no `candidate`, no `choice`, and no arm carrying a server
 * refusal string. Wire codes are mapped to one of these tokens by the screen
 * BEFORE they reach this component, so no text from the network reaches the live
 * region either.
 */
export type CastAct =
  /** Nothing to say. The paragraph still renders, empty — see below. */
  | 'idle'
  | 'submitting'
  | 'nothing-marked'
  | 'ballot-closed'
  | 'refused'
  | 'unreachable'
  /**
   * The two failure arms of the BLANK-PAPER confirmation (cast/BlankConfirm.tsx),
   * which is a screen of its own with no rows on it.
   *
   * They are new LITERALS rather than a reuse of `refused` / `unreachable`
   * because both of those end _"Your marks are still here"_ — a sentence about
   * rows that have been unmounted, said to somebody who is in the middle of
   * returning a paper with no marks on it. Note what they still are: a fixed
   * word naming the ACT and its outcome. Neither says, or could say, what is or
   * is not marked; the union is still literal-only and still carries no payload.
   */
  | 'blank-refused'
  | 'blank-unreachable'
  /** The fixture preview on the motion paper, which records nothing. */
  | 'preview';

/**
 * One sentence per act, and every one of them is a literal.
 *
 * THE FAILURE COPY STATES THE FACT BEFORE ANY BUTTON IS OFFERED —
 * CAST-PATH.md:233: _"Nothing has been recorded yet. Your marks are still
 * here."_ — "because a retry button offered first makes a person jab at a phone
 * instead of raising their hand". The screen renders this line ABOVE the action
 * bar for the same reason: on a phone that bar is pinned over the bottom of the
 * scroll port, and a message underneath it is a message nobody reads.
 *
 * `nothing-marked` says what was NOT sent rather than what is or is not on the
 * paper. It is reached before any request is made, so nothing has left the
 * device and there is nothing to disclose. **It now names the other lawful way
 * off this paper** (registrar ruling 2026-08-27: a member may deliberately
 * return a blank paper), because "choose one row" on its own became a false
 * statement about the member's options the moment that act existed.
 *
 * The two `blank-*` arms are the same two sentences as `refused` and
 * `unreachable` with their last clause changed, and the change is the whole
 * reason they exist — see {@link CastAct}.
 */
const ACT_COPY: Record<CastAct, string> = {
  idle: '',
  submitting: 'Sending your paper…',
  'nothing-marked': 'Nothing was sent. Mark one row and submit, or return this paper blank.',
  'ballot-closed': 'Voting has closed. Nothing was recorded.',
  refused: 'That was refused, and nothing has been recorded. Your marks are still here.',
  unreachable: 'Nothing has been recorded yet. Your marks are still here.',
  'blank-refused':
    'That was refused, and nothing has been recorded. Your paper has not been returned.',
  'blank-unreachable': 'Nothing has been recorded yet. Your paper has not been returned.',
  preview: 'Nothing was recorded. This is a preview of the ballot paper, on invented data.',
};

/**
 * The status line.
 *
 * It renders even when there is nothing to say, and it is mounted for the whole
 * life of the paper. Both are deliberate: a live region has to be in the DOM
 * BEFORE its text changes for the change to be announced, so a paragraph that
 * appears along with its first message announces nothing at all on several
 * screen readers. `.ballot-status` reserves the line's height so the page does
 * not jump when it fills.
 *
 * `role="status"` — polite — and never `role="alert"`. A submission landing is
 * not an alarm, and `alert` interrupts a person mid-sentence.
 *
 * **The successful cast is NOT one of these arms.** A recorded vote replaces the
 * paper with its own panel: leaving the marked row on screen under a line saying
 * "recorded" would be showing the member their vote after submit, which is the
 * same disclosure by a slower route.
 */
export function CastStatus({ act }: { act: CastAct }): JSX.Element {
  return (
    <p className="ballot-status" role="status">
      {ACT_COPY[act]}
    </p>
  );
}
